import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import type { CoeWatchlistResponse } from '../types';
import { CoeWatchlistReport } from './CoeWatchlistReport';
import {
  coeWatchlistBackPath,
  coeWatchlistListApiUrl,
  coeWatchlistTabLabel,
} from './coeWatchlistPrintMeta';

const CoeWatchlistReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();

  const listUrl = useMemo(() => coeWatchlistListApiUrl(searchParams), [searchParams]);
  const { data, error, isLoading } = useSWR<CoeWatchlistResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const withinDays = searchParams.get('withinDays')?.trim() || '90';
  const tab = searchParams.get('tab')?.trim() ?? '';
  const q = searchParams.get('q')?.trim() ?? '';

  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselName = useMemo(() => {
    if (!vesselId) return null;
    const match = (lookups?.vessels ?? []).find((v) => String(v.id) === vesselId);
    return match?.vesselName ?? `Vessel #${vesselId}`;
  }, [lookups?.vessels, vesselId]);

  const rows = useMemo(() => {
    let list = data?.rows ?? [];
    if (tab === 'overdue') list = list.filter((r) => r.intentionOverdue);
    if (tab === 'coeSoon') {
      list = list.filter((r) => r.daysToCoe != null && r.daysToCoe >= 0);
    }
    const query = q.toLowerCase();
    if (query) {
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(query) ||
          (r.vessel && r.vessel.toLowerCase().includes(query)) ||
          (r.rank && r.rank.toLowerCase().includes(query)),
      );
    }
    return [...list].sort((a, b) => {
      const ad = a.daysToCoe ?? Number.POSITIVE_INFINITY;
      const bd = b.daysToCoe ?? Number.POSITIVE_INFINITY;
      if (ad !== bd) return ad - bd;
      return a.transactionId - b.transactionId;
    });
  }, [data?.rows, q, tab]);

  const backTo = coeWatchlistBackPath();
  const filterLabels = useMemo(
    () => ({
      vessel: vesselName,
      withinDays,
      tab: coeWatchlistTabLabel(tab),
      search: q || null,
    }),
    [q, tab, vesselName, withinDays],
  );

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading COE watchlist…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load COE watchlist.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to COE watchlist
        </Link>
      </div>
    );
  }

  const subtitleParts = [
    vesselName || 'All vessels',
    `${withinDays}d`,
    filterLabels.tab,
    `${rows.length} row${rows.length === 1 ? '' : 's'}`,
  ].filter(Boolean);

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to COE watchlist"
      title="COE watchlist"
      subtitle={subtitleParts.join(' · ')}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <CoeWatchlistReport data={data} rows={rows} filterLabels={filterLabels} />
    </ReportView>
  );
};

export default CoeWatchlistReportPage;
